/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useEffect, useState } from 'react';
import { Button, IconButton, Snackbar } from '@mui/material';
import { Close } from '@mui/icons-material';
import { connect, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import styled from 'styled-components';
import StyledProps from '../../common/theme/AppThemeInterface';
import isNil from 'lodash/isNil';
import { getFlightPathOnce } from '../../../store/terminals/actions';
import { PortalsEnum } from '../../../shared/entities/portal';

export interface FlightPathSnackbarContainerState {
  flightPathLoadingErrorSnackbarOpen: boolean;
}
export interface ContainerStateProps {
  selectedDatapathUuidFleetView?: string;
  isRequestingFlightPath: boolean;
  requestingFlightPathError?: string;
}

export interface ContainerDispatchProps {
  getFlightPathAsync: Function;
}

const SnackbarRetryButton = styled(Button)`
  color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
`;
const FlightPathSnackbar = styled(Snackbar)`
  > div {
    background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  }
`;
const FlightPathSnackbarContainer = (props: ContainerStateProps) => {
  const { requestingFlightPathError, selectedDatapathUuidFleetView } = props;
  const dispatch = useDispatch();
  const [flightPathLoadingErrorSnackbarOpen, setFlightPathLoadingErrorSnackbarOpen] = useState<boolean>(false);
  useEffect(() => {
    if (requestingFlightPathError) {
      setFlightPathLoadingErrorSnackbarOpen(true);
    }
  }, [requestingFlightPathError]);

  const handleFlightPathLoadingErrorSnackbarClose = () => {
    setFlightPathLoadingErrorSnackbarOpen(false);
  };

  const reloadFlightPath = () => {
    setFlightPathLoadingErrorSnackbarOpen(false);
    if (!isNil(selectedDatapathUuidFleetView)) {
      dispatch(getFlightPathOnce(selectedDatapathUuidFleetView));
    }
  };
  return (
    <div>
      <FlightPathSnackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={flightPathLoadingErrorSnackbarOpen}
        autoHideDuration={999999}
        onClose={handleFlightPathLoadingErrorSnackbarClose}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        message={<span id="message-id">Error loading flight path</span>}
        action={[
          <SnackbarRetryButton key="retry" size="small" onClick={reloadFlightPath}>
            RETRY
          </SnackbarRetryButton>,
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={handleFlightPathLoadingErrorSnackbarClose}
          >
            <Close />
          </IconButton>,
        ]}
      />
    </div>
  );
};

export default connect(
  (state: RootState): ContainerStateProps => ({
    isRequestingFlightPath: state.terminals.isRequestingFlightPath,
    requestingFlightPathError: state.terminals.requestingFlightPathError
      ? state.terminals.requestingFlightPathError[PortalsEnum.UNIFIED_PORTAL]
      : undefined,
    selectedDatapathUuidFleetView:
      state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList,
  }),
)(FlightPathSnackbarContainer);
